import {
  ChangeEventHandler,
  CSSProperties,
  FocusEventHandler,
  MouseEventHandler,
  useState
} from 'react';
import { InputBaseComponentProps } from '@mui/material';
import { AgColor } from '../../Styles/AgColor';
import AgTypography from '../../Styles/AgTypography';
import { useIntl } from 'react-intl';

export interface AgTextAreaProps {
  id?: string;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  caption?: string;
  value?: string;
  defaultValue?: string | number;
  maxLength?: number;
  autoComplete?: string;
  placeholder?: string;
  inputProps?: InputBaseComponentProps | undefined;
  onChange?:
    | ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  style?: CSSProperties | undefined;
  formStyle?: CSSProperties | undefined;
  error?: boolean;
  noStar?: boolean;
  optionalText?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  enterKeyPressed?: Function;
  autofocus?: boolean;
}

const AgTextArea = ({
  id,
  required,
  disabled,
  label,
  caption,
  value,
  defaultValue,
  autoComplete,
  placeholder = 'Placeholder',
  maxLength,
  onChange,
  style,
  formStyle,
  error,
  noStar,
  optionalText,
  onBlur,
  enterKeyPressed,
  autofocus
}: AgTextAreaProps) => {
  const messages = useIntl().formatMessage;
  const [hoverBorder, setHoverBorder] = useState<boolean>(false);
  const [focusBorder, setFocusBorder] = useState<boolean>(false);
  const handleHoverEnter: MouseEventHandler<HTMLTextAreaElement> = () => {
    setHoverBorder(true);
  };

  const handleHoverLeave: MouseEventHandler<HTMLTextAreaElement> = () => {
    setHoverBorder(false);
  };
  const handleFocus: FocusEventHandler<HTMLTextAreaElement> = () => {
    setFocusBorder(true);
  };

  const handleBlur: FocusEventHandler<HTMLTextAreaElement> = (e) => {
    setFocusBorder(false);
    onBlur && onBlur(e);
  };

  const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      e.preventDefault();
      enterKeyPressed && enterKeyPressed();
    }
  };

  return (
    <AgTypography
      style={{ display: 'flex', flexDirection: 'column', ...formStyle }}
    >
      <AgTypography
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          height: '20px'
        }}
      >
        <label
          htmlFor={id}
          style={{
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '4%',
            fontFamily: 'Roboto, sans-serif',
            color: disabled ? AgColor.grey6 : AgColor.grey8,
            fontWeight: 500
          }}
        >
          {required && !noStar ? label + ' *' : label}
        </label>
        {!required && optionalText && (
          <AgTypography
            contentStyle={'AP.Assistive.Text.Regular'}
            message={messages({ id: 'STORYBOOK_OPTIONAL' })}
          />
        )}
      </AgTypography>
      <textarea
        autoFocus={autofocus}
        id={id}
        disabled={disabled}
        required={required}
        value={value}
        onChange={onChange}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        maxLength={maxLength}
        defaultValue={defaultValue}
        autoComplete={autoComplete}
        placeholder={placeholder}
        onKeyDown={(e) => onEnterPress(e)}
        style={{
          backgroundColor: disabled ? AgColor.grey3 : AgColor.grey1,
          border: '1px solid',
          borderColor: disabled
            ? AgColor.grey3
            : error
              ? AgColor.errorMain
              : hoverBorder || focusBorder
                ? AgColor.link1
                : AgColor.grey4,
          borderRadius: '6px',
          color: disabled ? AgColor.grey6 : AgColor.grey9,
          padding: '8px 16px',
          fontFamily: 'Inter, sans-serif',
          marginTop: '5px',
          fontSize: '16px',
          fontWeight: 400,
          fontStyle: 'normal',
          lineHeight: '26px',
          outline: 'none',
          ...style
        }}
      />
      <AgTypography
        id={id}
        contentStyle={'AP.Assistive.Text.Regular'}
        style={{
          marginLeft: '0',
          color: disabled
            ? AgColor.grey6
            : error
              ? AgColor.errorMain
              : AgColor.grey6
        }}
      >
        {caption}
      </AgTypography>
    </AgTypography>
  );
};

export default AgTextArea;
